import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { messageAPI, MessageType } from '../../api';
import { formatDate } from '../../utils/dateUtils';

const MessageContainer = styled.div`
  margin: 20px 0;
`;

const MessageListTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 20px;
  color: #333;
`;

const MessageList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

interface ReadProps {
  isRead: boolean;
}

const MessageItem = styled.li<ReadProps>`
  padding: 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  background-color: ${(props) => (props.isRead ? 'transparent' : '#e6f2ff')};
  border-left: ${(props) => (props.isRead ? 'none' : '4px solid #007bff')};
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const MessageTitle = styled.div<ReadProps>`
  font-weight: ${(props) => (props.isRead ? 'normal' : 'bold')};
  margin-bottom: 5px;
  display: flex;
  justify-content: flex-start;
  color: ${(props) => (props.isRead ? '#333' : '#0056b3')};
`;

const UnreadBadge = styled.span`
  background-color: #ff4757;
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-right: 8px;
  font-weight: bold;
`;

const MessageDate = styled.span`
  font-size: 12px;
  color: #777;
  font-weight: normal;
`;

const MessageSender = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
`;

const MessageDetail = styled.div`
  border: 1px solid #eee;
  padding: 20px;
  margin-top: 20px;
  border-radius: 5px;
  background-color: #fff;
`;

const MessageDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

const MessageDetailTitle = styled.h3`
  margin: 0;
  font-size: 18px;
`;

const MessageDetailInfo = styled.div`
  margin-bottom: 15px;
  font-size: 14px;
  color: #666;
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  line-height: 1.6;
  color: #333;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
`;

const Button = styled.button`
  padding: 8px 15px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background-color: #d32f2f;
  }
`;

const BackButton = styled.button`
  padding: 8px 15px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 10px;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

interface ActiveProps {
  isActive: boolean;
}

const PageButton = styled.button<ActiveProps>`
  padding: 5px 10px;
  margin: 0 5px;
  background-color: ${(props) => (props.isActive ? '#007bff' : '#f5f5f5')};
  color: ${(props) => (props.isActive ? 'white' : '#333')};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${(props) => (props.isActive ? '#0069d9' : '#e0e0e0')};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NoMessages = styled.div`
  text-align: center;
  padding: 30px;
  color: #777;
`;

const MyMessages: React.FC = () => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MessageType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // 메시지 목록 조회
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await messageAPI.getMyMessages(currentPage);
      setMessages(response.data.messages || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      setError('쪽지 목록을 불러오는 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  // 초기 데이터 로드 및 페이지 변경 시 재로드
  useEffect(() => {
    fetchMessages();
  }, [currentPage]);
  
  // 메시지 선택 및 읽음 처리
  const handleSelectMessage = async (message: MessageType) => {
    if (message.is_read === 0) {
      try {
        const response = await messageAPI.readMessage(message.id);
        // 읽음 처리된 메시지로 업데이트
        setMessages(prev =>
          prev.map(m => (m.id === message.id ? { ...m, is_read: 1 } : m))
        );
        setSelectedMessage(response.data.message);
      } catch (err) {
        console.error(err);
      }
    } else {
      setSelectedMessage(message);
    }
  };
  
  // 메시지 삭제
  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;
    
    if (window.confirm('정말로 이 쪽지를 삭제하시겠습니까?')) {
      try {
        await messageAPI.deleteMessage(selectedMessage.id);
        setMessages(prev => prev.filter(m => m.id !== selectedMessage.id));
        setSelectedMessage(null);
      } catch (err) {
        console.error(err);
      }
    }
  };
  
  // 메시지 목록으로 돌아가기
  const handleBackToList = () => {
    setSelectedMessage(null);
  };
  
  // 페이지 이동
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  if (loading && messages.length === 0) {
    return <div>로딩 중...</div>;
  }
  
  if (error) {
    return <div>{error}</div>;
  }
  
  return (
    <MessageContainer>
      {selectedMessage ? (
        <MessageDetail>
          <MessageDetailHeader>
            <MessageDetailTitle>{selectedMessage.title}</MessageDetailTitle>
          </MessageDetailHeader>
          
          <MessageDetailInfo>
            <div>보낸 사람: {selectedMessage.sender_username}</div>
            <div>수신 일시: {formatDate(selectedMessage.created_at)}</div>
            {selectedMessage.read_at && (
              <div>읽은 일시: {formatDate(selectedMessage.read_at)}</div>
            )}
          </MessageDetailInfo>
          
          <MessageContent>{selectedMessage.content}</MessageContent>
          
          <ButtonsContainer>
            <BackButton onClick={handleBackToList}>목록으로</BackButton>
            <Button onClick={handleDeleteMessage}>삭제</Button>
          </ButtonsContainer>
        </MessageDetail>
      ) : (
        <>
          {messages.length === 0 ? (
            <NoMessages>받은 쪽지가 없습니다.</NoMessages>
          ) : (
            <>
              <MessageList>
                {messages.map((message) => (
                  <MessageItem
                    key={message.id}
                    isRead={message.is_read === 1}
                    onClick={() => handleSelectMessage(message)}
                  >
                    <MessageTitle isRead={message.is_read === 1}>
                      {message.is_read === 0 && <UnreadBadge>읽지않음</UnreadBadge>}
                      {message.title}
                      <div style={{ flex: 1 }} />
                      <MessageDate>{formatDate(message.created_at)}</MessageDate>
                    </MessageTitle>
                    <MessageSender>보낸 사람: {message.sender_username}</MessageSender>
                  </MessageItem>
                ))}
              </MessageList>
              
              {totalPages > 1 && (
                <Pagination>
                  <PageButton
                    disabled={currentPage === 1}
                    isActive={false}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    이전
                  </PageButton>
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <PageButton
                      key={page}
                      isActive={currentPage === page}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </PageButton>
                  ))}
                  
                  <PageButton
                    disabled={currentPage === totalPages}
                    isActive={false}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    다음
                  </PageButton>
                </Pagination>
              )}
            </>
          )}
        </>
      )}
    </MessageContainer>
  );
};

export default MyMessages; 